import { useState } from 'react';
import { androidBridge, fullPageRedirect } from '@hungryroot/environment';
import { useExperiment } from '@hungryroot/experiments';
import { ArrowLeft, Gift } from '@hungryroot/icons';
import { Paragraph, Heading, Button } from '@hungryroot/ramen';
import { StatusCode } from '@hungryroot/rest';
import { postAcceptCancelWinback as acceptCancelWinback } from '@hungryroot/rest/api/accept_cancel_winback/post';
import { CreditMessage } from '../../components/CreditMessage/CreditMessage';
import { TextSection } from '../../components/TextSection/TextSection';
import {
  PromoType,
  type CancelWinbackResponse,
  type Promo,
} from '../../hooks/useGetCancelWinback';
import { iosPostMessage } from '../../utils/iosPostMessage';
import styles from './Winback.module.css';
import pageStyles from '../page.module.css';

interface Props {
  testid?: string;
  onNext: () => void;
  onPrev: () => void;
  promo?: CancelWinbackResponse;
}

function getPromoOffer(promo: Promo) {
  const value = parseInt(promo.value).toFixed();
  switch (promo.type) {
    case PromoType.FLAT_DISCOUNT:
      return `$${value} off`;
    default:
      return `${value}% off`;
  }
}

async function onOfferAccepted({
  promo,
  mhrRedesignVariant,
}: {
  promo: CancelWinbackResponse;
  mhrRedesignVariant: number;
}) {
  const { promo_code, promo_data } = promo;

  if (promo_code === null || promo_data === null) {
    return;
  }

  const res = await acceptCancelWinback({ promo_code });

  if (res.status === StatusCode.SuccessOK) {
    // Make message available to iOS
    iosPostMessage({ cancel_winback: promo_data });
    androidBridge('acceptedCancelWinback');

    if (mhrRedesignVariant > 0) {
      return fullPageRedirect('/myhungryroot/?toast=winback-offer-claimed');
    } else {
      return fullPageRedirect('/myhungryroot/?winback-confirmation=1');
    }
  }
}

export function Winback(props: Props) {
  const { testid, onNext, onPrev, promo } = props;
  const [isLoading, setIsLoading] = useState(false);
  const mhrRedesignVariant = useExperiment(171);

  if (mhrRedesignVariant === undefined) {
    return null;
  }

  const { promo_data } = promo || {};
  const { promos } = promo_data || {};
  const customerPromo = promos ? promos[0] : null;
  const { effect: text } = customerPromo || {};

  const promoOffer = promos ? getPromoOffer(promos[0]) : null;

  return (
    <div data-testid={testid} className={pageStyles.container}>
      <Button
        onClick={onPrev}
        style="default"
        testid={`${testid}-prev`}
        size="medium"
        prominence="tertiary"
        icon={<ArrowLeft />}
        iconPosition="left"
        textPrimary="Back"
        className={pageStyles.back}
      />
      <TextSection
        testid={`${testid}-text-section`}
        heading="Wait! Are you sure?"
        body="We’ve got a special offer just for you. Get major savings and hold onto other good stuff."
        className={pageStyles.text}
      />
      <CreditMessage
        testid={`${testid}-credit-message`}
        className={pageStyles.credit}
      />
      <div className={pageStyles.content}>
        {promoOffer ? (
          <div className={pageStyles['content-inner']}>
            <div className={styles.heading}>
              <Gift />
              <div>
                <Heading variant="H2" className={styles['heading-text']}>
                  {promoOffer}
                </Heading>
                <Paragraph variant="lead">{text}</Paragraph>
              </div>
            </div>
            <Paragraph>
              Claim this special offer to save time and money on food.
            </Paragraph>
            <Button
              onClick={() => {
                if (!promo) {
                  return;
                }
                setIsLoading(true);
                onOfferAccepted({ promo, mhrRedesignVariant });
              }}
              style="success"
              testid={`${testid}-accept`}
              size="medium"
              prominence="primary"
              textPrimary="CLAIM MY OFFER"
              className={pageStyles.accept}
              loading={isLoading}
            />
          </div>
        ) : null}
      </div>
      <Button
        onClick={onNext}
        style="default"
        testid={`${testid}-next`}
        size="medium"
        prominence="secondary"
        textPrimary="Continue to Cancel"
        className={pageStyles.confirm}
      />
    </div>
  );
}
